import { trapFocus } from '@/utils/Utils';
import { ReactNode, useCallback, useEffect, useId, useRef } from 'react';

interface AppModalProps {
    show: boolean;
    title: string;
    onClose: () => void;
    children: ReactNode;
    className?: string;
    headerActions?: ReactNode;
    acceptButton?: ReactNode;
    rejectButton?: ReactNode;
}

export const AppModal = ({
    show,
    title,
    onClose,
    children,
    className,
    headerActions,
    acceptButton,
    rejectButton,
}: AppModalProps) => {
    const dialogRef = useRef<HTMLDialogElement | null>(null);
    const previousActiveElementRef = useRef<HTMLElement | null>(null);
    const titleId = useId();
    const hasActions = Boolean(rejectButton || acceptButton);
    const closeModal = useCallback(() => {
        const previousActiveElement = previousActiveElementRef.current;
        onClose();
        if (previousActiveElement) {
            requestAnimationFrame(() => {
                if (previousActiveElement.isConnected) previousActiveElement.focus();
            });
        }
    }, [onClose]);
    const closeButton = (
        <button
            type="button"
            className="c-app-modal__close c-app-modal__close--with-label"
            onClick={closeModal}
            aria-label="Lukk dialog"
        >
            <i className="material-icons c-app-modal__close-icon" aria-hidden="true">
                close
            </i>
            <span className="c-app-modal__close-label">Lukk</span>
        </button>
    );

    useEffect(() => {
        const dialog = dialogRef.current;
        if (!dialog) return;

        const handleCancel = (event: Event) => {
            event.preventDefault();
            closeModal();
        };

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                event.preventDefault();
                closeModal();
            }
        };

        const handleBackdropClick = (event: MouseEvent) => {
            if (event.target === dialog) {
                closeModal();
            }
        };

        dialog.addEventListener('cancel', handleCancel);
        dialog.addEventListener('keydown', handleKeyDown);
        dialog.addEventListener('click', handleBackdropClick);

        if (show && !dialog.open) {
            previousActiveElementRef.current =
                document.activeElement instanceof HTMLElement &&
                document.activeElement !== document.body
                    ? document.activeElement
                    : null;
            dialog.showModal();
            trapFocus(dialog);
        }

        if (!show && dialog.open) {
            dialog.close();
        }

        return () => {
            dialog.removeEventListener('cancel', handleCancel);
            dialog.removeEventListener('keydown', handleKeyDown);
            dialog.removeEventListener('click', handleBackdropClick);
        };
    }, [closeModal, show]);

    return (
        <dialog
            ref={dialogRef}
            className={`c-app-modal ${className ?? ''}`.trim()}
            aria-labelledby={titleId}
            aria-modal="true"
        >
            <div className="c-app-modal__surface">
                <header
                    className={`c-app-modal__header${
                        headerActions ? ' c-app-modal__header--with-actions' : ''
                    }`}
                >
                    <h2 id={titleId} className="c-app-modal__title">
                        {title}
                    </h2>
                    {headerActions ? (
                        <div className="c-app-modal__header-actions">
                            {headerActions}
                            {closeButton}
                        </div>
                    ) : (
                        closeButton
                    )}
                </header>
                <div
                    className={`c-app-modal__content${
                        hasActions ? ' c-app-modal__content--with-actions' : ''
                    }`}
                >
                    {children}
                </div>
                {hasActions && (
                    <footer className="c-app-modal__actions">
                        {rejectButton && (
                            <div className="c-app-modal__action">{rejectButton}</div>
                        )}
                        {acceptButton && (
                            <div className="c-app-modal__action">{acceptButton}</div>
                        )}
                    </footer>
                )}
            </div>
        </dialog>
    );
};

export default AppModal;
